import { useState, useCallback, useEffect } from 'react';
import {
  getAboveNode,
  getEndPoint,
  getPluginOptions,
  getPluginType,
  getStartPoint,
  findNode,
  useComposedRef,
  useEditorRef,
  useHotkeys,
  usePlateSelectors,
  triggerFloatingLinkEdit,
  floatingLinkActions,
  floatingLinkSelectors,
  useFloatingLinkSelectors,
  useVirtualFloatingLink,
  ELEMENT_LINK,
  getDefaultBoundingClientRect,
  getRangeBoundingClientRect,
  getNode,
  select,
} from '@udecode/plate';

// import { useDetectClickOutside } from 'hooks/useDetectClickOutside';
import { submitFloatingLink } from './submitFloatingLink';
import { useFloatingLinkEnter } from './useFloatingLinkEnter';
import { useFloatingLinkEscape } from './useFloatingLinkEscape';

export const useFloatingLinkEdit = ({
  floatingOptions,
  ...props
}) => {
  const editor = useEditorRef();
  const keyEditor = usePlateSelectors().keyEditor();
  const mode = useFloatingLinkSelectors().mode();
  const open = useFloatingLinkSelectors().isOpen(editor.id);

  const [prevSelection, setPrevSelection] = useState();

  const { triggerFloatingLinkHotkeys } = getPluginOptions(
    editor,
    ELEMENT_LINK,
  );

  const getBoundingClientRect = useCallback(() => {
    const entry = getAboveNode(editor, {
      match: { type: getPluginType(editor, ELEMENT_LINK) },
    });

    if (entry) {
      const [, path] = entry;
      return getRangeBoundingClientRect(editor, {
        anchor: getStartPoint(editor, path),
        focus: getEndPoint(editor, path),
      });
    }

    return getDefaultBoundingClientRect();
  }, [editor]);

  const isOpen = open && mode === 'edit';

  /* #TODO: If we want to dismiss edit floating element on click
   * on click ouside of the editor context, implement this.
  const ref = useDetectClickOutside({
    onTriggered: (e) => {
      console.log('e', e);
    },
  });
  */

  const { update, style, refs } = useVirtualFloatingLink({
    editorId: editor.id,
    open: isOpen,
    getBoundingClientRect,
    ...floatingOptions,
  });

  useEffect(() => {
    const updateLink = () => {
      if (prevSelection) {
        const newSelection = editor.selection;
        const { anchor, focus } = prevSelection;
        // Add arbitrary delay because sometimes the select check doesn't return accurately due to timing.
        setTimeout(() => {
          if (getNode(editor, anchor.path) && getNode(editor, focus.path)) {
            select(editor, prevSelection);
          }
          if (!submitFloatingLink(editor)) {
            floatingLinkActions.hide();
          }
          select(editor, newSelection);
          setPrevSelection(null);
        }, 0);
      }
    };
    // If clicking on a node and it's a link.
    const linkNode = findNode(editor, {
      match: { type: getPluginType(editor, ELEMENT_LINK) },
    });
    if (editor.selection && linkNode) {
      updateLink();
      floatingLinkActions.show('edit', editor.id);
      setPrevSelection(editor.selection);
      update();
      return;
    }
    // If edit window was opened, dismiss it.
    if (floatingLinkSelectors.mode() === 'edit') {
      updateLink();
      floatingLinkActions.hide();
      setPrevSelection(null);
    }
  }, [editor, keyEditor, update]);

  useHotkeys(
    triggerFloatingLinkHotkeys,
    (e) => {
      if (
        floatingLinkSelectors.mode() === 'edit' &&
        triggerFloatingLinkEdit(editor)
      ) {
        e.preventDefault();
      }
    },
    {
      enableOnContentEditable: true,
    },
    [],
  );

  useFloatingLinkEnter();

  useFloatingLinkEscape();

  return {
    style: {
      ...style,
      zIndex: 42,
    },
    ...props,
    ref: useComposedRef(props.ref, refs.setFloating),
  };
};
